import React, { useEffect, useState } from 'react';

import { Box, HStack, Icon, Link, Text, VStack } from '@chakra-ui/react';
import { FiAlignLeft } from 'react-icons/fi';

import { DocumentationHeading } from 'api/documentation';

/** Room below the app header before a heading counts as the current section. */
const ACTIVE_MARGIN = 40;

/**
 * Height of the app's sticky header, which covers the top of the scrolling
 * content. Sticky panels and section jumps sit below it.
 */
export const useAppHeaderHeight = () => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('[data-app-header]');
    if (!header) return;
    const update = () => setHeight(header.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  return height;
};

export const tocHeadings = (headings: DocumentationHeading[]) =>
  headings.filter(heading => heading.level <= 3);

/**
 * The section being read: the last heading scrolled above the top of the
 * viewport, or undefined while the reader is still above the first one.
 */
export const useActiveHeading = (
  headings: DocumentationHeading[],
  headerHeight = 0,
) => {
  const [activeId, setActiveId] = useState<string>();

  useEffect(() => {
    const items = tocHeadings(headings);
    const update = () => {
      let current: string | undefined;
      items.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (
          element &&
          element.getBoundingClientRect().top <= headerHeight + ACTIVE_MARGIN
        )
          current = id;
      });
      setActiveId(current);
    };
    update();
    // The app scrolls an inner container, so listen in the capture phase.
    document.addEventListener('scroll', update, {
      capture: true,
      passive: true,
    });
    return () =>
      document.removeEventListener('scroll', update, { capture: true });
  }, [headerHeight, headings]);

  return [activeId, setActiveId] as const;
};

interface Props {
  headings: DocumentationHeading[];
  activeId?: string;
  onSelect: (id: string) => void;
}

/** A page's sections as links, with the one being read highlighted. */
export const DocumentationSectionLinks: React.FC<Props> = ({
  headings,
  activeId,
  onSelect,
}) => {
  const items = tocHeadings(headings);
  const highlighted = activeId ?? items[0]?.id;
  return (
    <VStack
      align="stretch"
      spacing={0}
      borderLeft="1px solid"
      borderColor="gray.100"
    >
      {items.map(({ id, text, level }) => {
        const isActive = id === highlighted;
        return (
          <Link
            key={id}
            href={`#${id}`}
            onClick={event => {
              event.preventDefault();
              onSelect(id);
            }}
            ml="-1px"
            py={1.5}
            pl={level === 3 ? 6 : 3}
            fontSize="14px"
            lineHeight={1.45}
            borderLeft="2px solid"
            borderColor={isActive ? 'primary' : 'transparent'}
            color={isActive ? 'primary' : 'gray.500'}
            transition="color 150ms, border-color 150ms"
            _hover={{ color: 'primary', textDecoration: 'none' }}
          >
            {text}
          </Link>
        );
      })}
    </VStack>
  );
};

/** "On this page": the page's sections, with the one being read highlighted. */
export const DocumentationToc: React.FC<Props> = ({
  headings,
  activeId,
  onSelect,
}) => {
  if (!tocHeadings(headings).length) return null;

  return (
    <Box as="nav" aria-label="On this page">
      <HStack spacing={2} mb={3} color="gray.500">
        <Icon as={FiAlignLeft} boxSize={3.5} />
        <Text
          fontSize="13px"
          fontWeight={600}
          letterSpacing="0.06em"
          textTransform="uppercase"
        >
          On this page
        </Text>
      </HStack>
      <DocumentationSectionLinks
        headings={headings}
        activeId={activeId}
        onSelect={onSelect}
      />
    </Box>
  );
};
